/**
 * Conversation State Machine - Makes Invalid States Unrepresentable
 *
 * Design Principle: Use TypeScript's discriminated unions to enforce correct state handling.
 * The compiler prevents bugs by making it impossible to:
 * - Check backend sync on newly created conversations
 * - Send messages in orphaned (read-only) conversations
 * - Forget to handle sync checks on restored conversations
 * - Transition between invalid states
 *
 * State Lifecycle:
 *
 *   DRAFT ──────────────────┐
 *     │                      │
 *     │ (sync to backend)    │ (IndexedDB restore)
 *     ↓                      ↓
 *   SYNCED               RESTORED
 *     │                      │
 *     │                      │ (backend check)
 *     │                      ├──→ SYNCED (backend has it)
 *     │                      └──→ ORPHANED (backend missing)
 *     │
 *     │ (backend deletion)
 *     └──→ ORPHANED
 */

/**
 * Why the conversation might be orphaned (backend doesn't have it)
 */
export type OrphanReason =
  | 'backend_expired'      // Backend Redis TTL expired (24+ hours)
  | 'backend_deleted'      // Manually deleted from backend
  | 'never_synced'         // Created locally but never sent to backend
  | 'sync_failed';         // Sync attempts failed

/**
 * Discriminated union - compiler enforces exhaustive handling
 */
export type ConversationState =
  | DraftConversation
  | SyncedConversation
  | RestoredConversation
  | OrphanedConversation;

/**
 * DRAFT: Local conversation, not yet synced to backend
 * - Can add messages
 * - Can send messages (will auto-sync on first send)
 * - No backend sync check needed
 */
export interface DraftConversation {
  readonly kind: 'DRAFT';
  readonly id: string;
  readonly createdAt: Date;
  readonly messageCount: number;
}

/**
 * SYNCED: Backend has confirmed this conversation exists
 * - Can add messages
 * - Can send messages
 * - Backend sync is current
 */
export interface SyncedConversation {
  readonly kind: 'SYNCED';
  readonly id: string;
  readonly lastSyncedAt: Date;
  readonly backendMessageCount: number;
}

/**
 * RESTORED: Loaded from IndexedDB, backend status unknown
 * - CAN send messages immediately (don't block user!)
 * - needsSyncCheck flag forces background sync to update state
 * - Sync check is informational only (determines banner state)
 * - Temporary state - will transition to SYNCED or ORPHANED
 */
export interface RestoredConversation {
  readonly kind: 'RESTORED';
  readonly id: string;
  readonly messageCount: number;
  readonly restoredAt: Date;
  readonly needsSyncCheck: true;  // Compiler forces handling
}

/**
 * ORPHANED: Backend doesn't have this conversation
 * - CAN send messages (local-first!)
 * - First message sent auto-resurrects to DRAFT and uploads
 * - Banner shows informational message about local-only state
 * - User can continue working offline, messages queued for sync
 */
export interface OrphanedConversation {
  readonly kind: 'ORPHANED';
  readonly id: string;
  readonly messageCount: number;
  readonly reason: OrphanReason;
  readonly detectedAt: Date;
}

/**
 * Type guards for type-safe state checking
 */
export function isDraft(state: ConversationState): state is DraftConversation {
  return state.kind === 'DRAFT';
}

export function isSynced(state: ConversationState): state is SyncedConversation {
  return state.kind === 'SYNCED';
}

export function isRestored(state: ConversationState): state is RestoredConversation {
  return state.kind === 'RESTORED';
}

export function isOrphaned(state: ConversationState): state is OrphanedConversation {
  return state.kind === 'ORPHANED';
}

/**
 * Check if conversation allows sending messages
 * Compiler ensures all cases are handled
 */
export function canSendMessages(state: ConversationState): boolean {
  switch (state.kind) {
    case 'DRAFT':
    case 'SYNCED':
    case 'RESTORED':  // ✅ Allow immediate use, sync check is background task
    case 'ORPHANED':  // ✅ Local-first: allow offline work
      return true;

    default:
      // TypeScript ensures exhaustive checking
      const _exhaustive: never = state;
      return false;
  }
}

/**
 * Check if conversation is in read-only mode
 */
export function isReadOnly(state: ConversationState): boolean {
  return state.kind === 'ORPHANED';
}

/**
 * Check if conversation needs user action
 */
export function needsUserAction(state: ConversationState): boolean {
  return state.kind === 'ORPHANED';
}
